// Declare Office as global (available in Office Add-in context)
declare const Office: any;

/**
 * Interface for dialog size configuration
 */
export interface DialogSize {
  height: number;
  width: number;
}

/**
 * Get optimal dialog size based on platform and environment for image viewer
 * This function provides platform-specific sizing for Office Add-in dialogs
 * to ensure proper display across different environments (Windows, Mac, Web)
 */
export const getDialogSize = (): DialogSize => {
  const platform = Office?.context?.platform;
  const isMac = platform === Office.PlatformType.Mac;
  const isWeb = platform === Office.PlatformType.OfficeOnline;
  
  // Base sizes for image viewer dialog (larger than other dialogs)
  let height = 50;
  let width = 42;
  
  // Platform-specific adjustments
  if (isMac) {
    // macOS needs larger values due to different UI scaling
    height = 60;
    width = 50;
  } else if (isWeb) {
    // Excel Web in browsers - optimize for browser environment
    height = 55;
    width = 45;
  } else {
    // Windows Desktop - check for high DPI displays
    const devicePixelRatio = window.devicePixelRatio || 1;
    if (devicePixelRatio > 1.5) {
      // High DPI displays (4K, high-res laptops) - increase for better visibility
      height += 8;
      width += 5;
    }
  }
  
  // Ensure reasonable bounds for image viewer (larger than form dialogs)
  height = Math.min(Math.max(height, 45), 75);
  width = Math.min(Math.max(width, 35), 60);
  
  return { height, width };
};

/**
 * Get optimal dialog size for smaller form dialogs (non-image viewer)
 * This function provides sizing for standard form dialogs like activation, company management, etc.
 */
export const getFormDialogSize = (): DialogSize => {
  const platform = Office?.context?.platform;
  const isMac = platform === Office.PlatformType.Mac;
  const isWeb = platform === Office.PlatformType.OfficeOnline;
  
  // Base sizes for form dialogs (smaller than image viewer)
  let height = 40;
  let width = 35;
  
  // Platform-specific adjustments
  if (isMac) {
    // macOS needs larger values due to different UI scaling
    height = 45;
    width = 40;
  } else if (isWeb) {
    // Excel Web in browsers - optimize for browser environment
    height = 42;
    width = 37;
  } else {
    // Windows Desktop - check for high DPI displays
    const devicePixelRatio = window.devicePixelRatio || 1;
    if (devicePixelRatio > 1.5) {
      // High DPI displays (4K, high-res laptops) - increase for better visibility
      height += 5;
      width += 3;
    }
  }
  
  // Ensure reasonable bounds for form dialogs
  height = Math.min(Math.max(height, 35), 60);
  width = Math.min(Math.max(width, 30), 50);
  
  return { height, width };
};